import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components';
import React from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { GoogleAnalyticsView } from './GoogleAnalyticsView';
import { useGoogleAnalyticsConnection } from './hooks/useGoogleAnalyticsConnection';
import { useGoogleAnalyticsConfigurationQuery } from './queries/useGoogleAnalyticsConfigurationQuery';
export const GoogleAnalyticsContainer = () => {
    const { open } = useModalContext();
    const { connectLink, disconnect } = useGoogleAnalyticsConnection();
    const [connectedParam, setConnectedParam] = useQueryParam('connected', StringParam);
    const isConnectedStatus = typeof connectedParam === 'string';
    const { googleAnalyticsConfigurationQuery } = useGoogleAnalyticsConfigurationQuery(undefined, {
        refetchInterval: (data) => isConnectedStatus && data?.type && ['NOT_CONNECTED', 'IN_PROGRESS'].includes(data.type)
            ? 2_000
            : false,
    });
    if (!googleAnalyticsConfigurationQuery.data ||
        googleAnalyticsConfigurationQuery.isInitialLoading) {
        return React.createElement(GoogleAnalyticsView.Skeleton, null);
    }
    const onDisconnect = () => {
        setConnectedParam(undefined);
        open(ConfirmModal, {
            title: 'Are you sure you want to disconnect from Google Analytics?',
            confirmLabel: 'Yes, disconnect',
            confirmAppearance: 'destructive',
            onConfirm: () => disconnect(() => {
                Toasts.notify('Disconnected from Google Analytics successfully.', { type: 'success' });
            }),
        });
    };
    const getProps = () => {
        switch (googleAnalyticsConfigurationQuery.data.type) {
            case 'NOT_CONNECTED':
                return {
                    title: 'Connect Google Analytics to see views and other advanced analytics',
                    description: 'See how many people visit your site along with other advanced analytics',
                    button: {
                        label: isConnectedStatus ? 'Connecting...' : 'Connect for free',
                        href: connectLink,
                        target: '_blank',
                        disabled: isConnectedStatus,
                    },
                };
            case 'PROVISIONED':
                return {
                    title: 'See page views and other advanced analytics',
                    description: (React.createElement(React.Fragment, null,
                        "Connected to ",
                        googleAnalyticsConfigurationQuery.data.email,
                        ' ',
                        React.createElement("button", { onClick: onDisconnect }, "Disconnect"))),
                    button: {
                        label: 'Accept Terms & Conditions',
                        href: googleAnalyticsConfigurationQuery.data.termsAndConditionsUri,
                    },
                };
            case 'IN_PROGRESS':
                return {
                    title: 'See page views and other advanced analytics',
                    description: (React.createElement(React.Fragment, null,
                        "Connected to ",
                        googleAnalyticsConfigurationQuery.data.email,
                        ' ',
                        React.createElement("button", { onClick: onDisconnect }, "Disconnect"))),
                    button: { label: 'Setup in progress...', disabled: true },
                };
            case 'CONNECTED':
                return {
                    title: 'See page views and other advanced analytics',
                    description: (React.createElement(React.Fragment, null,
                        "Connected to ",
                        googleAnalyticsConfigurationQuery.data.email,
                        ' ',
                        React.createElement("button", { onClick: onDisconnect }, "Disconnect"))),
                    button: {
                        label: 'View Google Analytics',
                        href: googleAnalyticsConfigurationQuery.data.googleAnalyticsDashboardUri,
                        target: '_blank',
                    },
                };
            case 'FAILED':
                return {
                    title: 'See page views and other advanced analytics',
                    description: (React.createElement(React.Fragment, null,
                        "Connected to ",
                        googleAnalyticsConfigurationQuery.data.email,
                        ' ',
                        React.createElement("button", { onClick: onDisconnect }, "Disconnect"))),
                    banner: {
                        title: {
                            MAKE_SURE_TO_BE_LOGGED_TO_PROPER_ACCOUNT: 'We encountered problem during setup. Please make sure to use the same Google account for accepting Terms & Conditions and for account setup. Reconnect and try again.',
                            MATCHING_CONFIGURATION_NOT_FOUND_FOR_MEASUREMENT_ID: `The Google Analytics account you’ve connected (${googleAnalyticsConfigurationQuery.data.email}) does not have permission to view the Google tracking pixel you entered on Settings -> Analytics. You can create a new tracking pixel by removing the current one from Settings -> Analytics or connect a different Google Analytics account that has access to that pixel.`,
                            MISSING_SCOPES: 'Missing access. Try reconnecting with Google Analytics to fix the issue. Make sure to give consent for accessing your account and data during connection process.',
                        }[googleAnalyticsConfigurationQuery.data.info[0].type],
                        appearance: 'danger',
                    },
                    issue: 'Connection issue',
                };
        }
    };
    return React.createElement(GoogleAnalyticsView, { ...getProps() });
};
